import { faEye, faPaperPlane } from "@fortawesome/pro-solid-svg-icons";
import { faFileInvoiceDollar } from "@fortawesome/pro-regular-svg-icons";

import { BillingDocumentType } from "constants/billingDocuments";

import {
  getPaymentAdviceReportUrl,
  getPaymentReferenceLabel,
  openEmailReportModal,
} from "lib/navigation";
import { formatDate } from "lib/timeFormats";

interface PaymentGroup {
  key: string;
  payment: Payment;
}

export function getPaymentAdviceDirection(
  documentType: string | undefined,
): string {
  return documentType === BillingDocumentType.RCTI ? "Remittance" : "Receipt";
}

export function groupPayments(payments: Payment[]): PaymentGroup[] {
  const seen = new Set<string>();
  const groups: PaymentGroup[] = [];
  for (const p of payments) {
    const key = p.batchPaymentId || p.id;
    if (!seen.has(key)) {
      seen.add(key);
      groups.push({ key, payment: p });
    }
  }
  return groups;
}

interface BuildPaymentAdviceButtonsParams {
  paymentGroups: PaymentGroup[];
  direction: string;
  userRoleSlug: string;
  emailBusinessId: string;
  saleId: string | null;
}

export function buildPaymentAdviceButtons({
  paymentGroups,
  direction,
  userRoleSlug,
  emailBusinessId,
  saleId,
}: BuildPaymentAdviceButtonsParams): object[] {
  if (paymentGroups.length === 0) {
    return [];
  }

  const buildUrl = (groups: PaymentGroup[]) =>
    getPaymentAdviceReportUrl({
      batchPaymentIds: groups
        .filter(({ payment }) => payment.batchPaymentId)
        .map(({ payment }) => payment.batchPaymentId),
      paymentIds: groups
        .filter(({ payment }) => !payment.batchPaymentId)
        .map(({ payment }) => payment.id),
      userRoleSlug,
    });

  const buildSendViewSubNavItems = (
    groups: PaymentGroup[],
    reportName: string,
  ) => [
    {
      dataTour: `sendPaymentAdvice-${reportName}`,
      icon: faPaperPlane,
      title: "Send",
      onClick: () =>
        openEmailReportModal(
          buildUrl(groups),
          reportName,
          emailBusinessId,
          undefined,
          undefined,
          saleId,
        ),
    },
    {
      dataTour: `viewPaymentAdvice-${reportName}`,
      icon: faEye,
      title: "View",
      onClick: () => window.open(buildUrl(groups)),
    },
  ];

  return [
    paymentGroups.length > 1 && {
      dataTour: "viewPaymentAdvice-all",
      icon: faFileInvoiceDollar,
      title: `All ${direction}s`,
      subNavItems: buildSendViewSubNavItems(paymentGroups, `All ${direction}s`),
    },
    ...paymentGroups.map(({ key, payment }) => {
      const label = `${direction} ${getPaymentReferenceLabel(payment)} (${formatDate(payment.paymentDate)})`;
      return {
        dataTour: `viewPaymentAdvice-${key}`,
        icon: faFileInvoiceDollar,
        title: label,
        subNavItems: buildSendViewSubNavItems([{ key, payment }], label),
      };
    }),
  ].filter(Boolean);
}
